import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { api, errMsg } from "../lib/api";

const MAX = 90;

export default function Voice({ onText, onError, label = "Tap to speak" }) {
  const [rec, setRec] = useState(false);
  const [sec, setSec] = useState(0);
  const [busy, setBusy] = useState(false);
  const canvas = useRef(null);
  const st = useRef({});

  useEffect(() => () => stopAll(), []);

  const stopAll = () => {
    const s = st.current;
    clearInterval(s.timer); cancelAnimationFrame(s.raf);
    s.stream?.getTracks().forEach((t) => t.stop());
    s.ctx?.close?.().catch(() => {});
  };

  const draw = () => {
    const s = st.current, c = canvas.current;
    if (!c || !s.an) return;
    const g = c.getContext("2d"), data = new Uint8Array(s.an.frequencyBinCount);
    s.an.getByteFrequencyData(data);
    g.clearRect(0, 0, c.width, c.height);
    const n = 28, w = c.width / n;
    for (let i = 0; i < n; i++) {
      const v = data[i * 2] / 255, h = Math.max(4, v * c.height);
      g.fillStyle = i % 2 ? "#FF6FA3" : "#6A5AE0";
      g.fillRect(i * w + 2, (c.height - h) / 2, w - 4, h);
    }
    s.raf = requestAnimationFrame(draw);
  };

  const browserFallback = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return onError?.("We couldn't hear that. Please try again or type instead.");
    const r = new SR(); r.lang = "hi-IN"; r.interimResults = false;
    r.onresult = (e) => onText(Array.from(e.results).map((x) => x[0].transcript).join(" "));
    r.onerror = () => onError?.("We couldn't hear that. Please type instead.");
    r.start();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = async () => {
        stopAll();
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        const ext = (mr.mimeType || "").includes("mp4") ? "m4a" : "webm";
        const fd = new FormData(); fd.append("audio", blob, `note.${ext}`);
        setBusy(true);
        try {
          const { data } = await api.post("/ai/transcribe", fd);
          if (data.text?.trim()) onText(data.text); else onError?.("We didn't catch anything. Please try again.");
        } catch (e) {
          onError?.(errMsg(e) + " Trying your phone's listener…"); browserFallback();
        } finally { setBusy(false); }
      };
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const an = ctx.createAnalyser(); an.fftSize = 128;
      ctx.createMediaStreamSource(stream).connect(an);
      st.current = { stream, mr, ctx, an, timer: setInterval(() => setSec((x) => { if (x + 1 >= MAX) mr.state === "recording" && mr.stop(); return x + 1; }), 1000) };
      setSec(0); setRec(true); mr.start(); draw();
    } catch (e) {
      browserFallback();
    }
  };

  const stop = () => { setRec(false); st.current.mr?.state === "recording" && st.current.mr.stop(); };

  return (
    <div className="ul-card p-5 flex flex-col items-center gap-3" data-testid="voice-recorder">
      <button data-testid={rec ? "voice-stop-btn" : "voice-record-btn"} disabled={busy} onClick={rec ? stop : start}
        className={`w-24 h-24 rounded-full flex items-center justify-center text-white ${rec ? "" : "ul-pulse"}`}
        style={{ background: rec ? "#FF6FA3" : "linear-gradient(135deg,#FF6FA3,#6A5AE0)" }}>
        {rec ? <Square size={34} fill="white" /> : <Mic size={40} />}
      </button>
      <canvas ref={canvas} width={280} height={48} className={rec ? "" : "opacity-30"} />
      <p className="font-display font-bold text-lg" data-testid="voice-timer">
        {busy ? <span className="ul-shimmer">Listening carefully…</span> : rec ? `0:${String(sec).padStart(2, "0")} / 1:30 · tap to stop` : label}
      </p>
    </div>
  );
}
